import {
  AppRoutes,
  logUserActivity,
  RouteName,
  useAppDispatch,
  useAppSelector,
  UserActivityAction,
  UserActivityActionSummary,
  UserActivityPageName,
} from 'common';
import { getRecipeMenuDiaryActionLabel } from 'common/src/helpers/general/recipe';
import { getMenuDiaries, revertMenuDiaryAction, selectMenuDiary } from 'common/src/store/recipes';
import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { BreadcrumbItem, CfBreadcrumbs } from '../../../cf-ui/Breadcrumbs/CfBreadcrumbs';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { getResolution } from '../../../utilities/resolution';
import DiaryGrid, { DiaryGridHistory, DiaryGridParams } from '../../_shared/components/DiaryGrid/DiaryGrid';
import { PageContentContainer, StyledViewBox } from '../../_shared/components/shared-styled-components.styles';

type MenuCustomerDiaryParams = {
  customerId: string;
};

export default function MenuCustomerDiary(): JSX.Element {
  const history = useHistory<DiaryGridHistory>();
  const { customerId } = useParams<MenuCustomerDiaryParams>();
  const { filter } = useQueryParams<DiaryGridParams>();

  const dispatch = useAppDispatch();

  const menuDiaries = useAppSelector((s) => selectMenuDiary(s, filter));
  const isLoading = useAppSelector((s) => s.menus.isMenuDiariesLoading);

  const canManageRecipes = useAppSelector(
    (s) => s.user.userSite?.AreaFeaturePermissionSetting.PermissionSetting.CanManageRecipes
  );

  useEffect(() => {
    dispatch(
      logUserActivity({
        action: UserActivityAction.Visit,
        pageName: UserActivityPageName.MenuCustomerDiary,
        actionSummary: UserActivityActionSummary.VisitedMenuCustomerDiary,
        resolution: getResolution(),
      })
    );
  }, []);

  useEffect(() => {
    dispatch(
      getMenuDiaries(
        customerId,
        getResolution(),
        undefined,
        history?.location?.state?.startDate,
        history?.location?.state?.endDate
      )
    );
  }, [customerId, history?.location?.state?.startDate, history?.location?.state?.endDate]);

  const revertAction = (recipeMenuDiaryId: string, onSuccess?: () => void) => {
    dispatch(revertMenuDiaryAction(recipeMenuDiaryId, getResolution(), onSuccess));
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      routeName: RouteName.RecipeDashboard,
      displayName: AppRoutes[RouteName.RecipeDashboard].DisplayName,
    },
    {
      routeName: RouteName.Menus,
      displayName: AppRoutes[RouteName.Menus].DisplayName,
      params: [customerId],
    },
    {
      routeName: RouteName.MenuCustomerDiary,
      displayName: AppRoutes[RouteName.MenuCustomerDiary].DisplayName,
      params: [customerId],
    },
  ];

  return (
    <StyledViewBox>
      <CfBreadcrumbs breadcrumbs={breadcrumbs} />
      <PageContentContainer>
        <DiaryGrid
          gridId='MenuCustomerDiaryGrid'
          title='Menus diary'
          isLoading={isLoading}
          onRevert={canManageRecipes ? revertAction : undefined}
          revertDialogType='menu'
          gridData={menuDiaries.map((d) => ({
            id: d.RecipeMenuDiaryId,
            action: getRecipeMenuDiaryActionLabel(d.ActionType),
            summary: d.ActionSummary,
            changeBy: d.ChangeByUser,
            logDate: d.ChangeDate,
            canRevert: d.CanRevert,
          }))}
        />
      </PageContentContainer>
    </StyledViewBox>
  );
}
